import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../../shared/material/material.module';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AccessPlan, AccessPlanDiscount, AccessPlanControllerService } from '../../../../core/generated';
import { CpfMaskPipe } from '../../../../shared/pipes/cpf-mask.pipe';
import { modalFadeIn } from '../../shared/animations';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { ExternalPayerService } from '../../external-payer/services/external-payer.service';
import { PlanUpgradeModalComponent } from '../plan-upgrade-modal/plan-upgrade-modal.component';
import { AddPayerDialogComponent } from '../add-payer-dialog/add-payer-dialog.component';
import { MatDialog } from '@angular/material/dialog';

/**
 * PlanDetailsModalComponent
 * Modal displaying complete plan details with items, discounts, and financial summary
 * Requirements: 4.1, 4.2, 4.3, 4.4, 4.5, 4.6, 13.1, 13.2, 11.4
 */
@Component({
  selector: 'app-plan-details-modal',
  standalone: true,
  imports: [MaterialModule, CommonModule, FormsModule, CpfMaskPipe, MatMenuModule, MatDividerModule],
  templateUrl: './plan-details-modal.component.html',
  styleUrl: './plan-details-modal.component.scss',
  animations: [modalFadeIn]
})
export class PlanDetailsModalComponent {
  dialogRef = inject(MatDialogRef<PlanDetailsModalComponent>);
  data = inject<{ plan: AccessPlan }>(MAT_DIALOG_DATA);

  private accessPlanService = inject(AccessPlanControllerService);
  private externalPayerService = inject(ExternalPayerService);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);

  isUpdatingAutoRenew = signal(false);
  isRequestingPayment = signal(false);

  // Estado do painel de edição de pagador
  isEditingPayer = signal(false);
  isSavingPayer = signal(false);
  payerCnpjInput = signal('');

  // Table columns for items (Requirement 4.4)
  displayedColumns = ['product', 'quantity', 'unitPrice', 'total'];

  /** Abre o campo inline para definir/alterar o payerCnpj */
  startEditPayer(): void {
    this.payerCnpjInput.set(this.data.plan.payerCnpj ?? '');
    this.isEditingPayer.set(true);
  }

  /** Cancela a edição sem salvar */
  cancelEditPayer(): void {
    this.isEditingPayer.set(false);
    this.payerCnpjInput.set('');
  }

  /** Salva o payerCnpj via POST /access-plans/{id}/add-external-payer */
  savePayer(): void {
    const id = this.data.plan.id;
    const cnpj = this.payerCnpjInput().trim();
    if (!id || !cnpj || this.isSavingPayer()) return;

    this.isSavingPayer.set(true);

    this.externalPayerService.addExternalPayer(id, cnpj).subscribe({
      next: (saved) => {
        this.data.plan.payerCnpj = saved.payerCnpj;
        this.data.plan.payerAccepted = saved.payerAccepted;
        this.isEditingPayer.set(false);
        this.isSavingPayer.set(false);
        this.snackBar.open('Pagador vinculado com sucesso! Aguardando aceitação.', 'Fechar', {
          duration: 4000,
          panelClass: ['success-snackbar']
        });
      },
      error: () => {
        this.snackBar.open('Erro ao vincular pagador. Verifique o CPF/CNPJ informado.', 'Fechar', { duration: 4000 });
        this.isSavingPayer.set(false);
      }
    });
  }

  /** Remove o pagador externo (limpa o payerCnpj) */
  removePayer(): void {
    const id = this.data.plan.id;
    if (!id) return;

    const updatedPlan: AccessPlan = { ...this.data.plan, payerCnpj: undefined, payerAccepted: false };
    this.accessPlanService.update17(id, updatedPlan).subscribe({
      next: (saved) => {
        this.data.plan.payerCnpj = saved.payerCnpj;
        this.data.plan.payerAccepted = saved.payerAccepted;
        this.snackBar.open('Pagador externo removido.', 'Fechar', { duration: 3000 });
      },
      error: () => {
        this.snackBar.open('Erro ao remover pagador.', 'Fechar', { duration: 4000 });
      }
    });
  }

  /**
   * Toggle auto renew for the plan
   */
  toggleAutoRenew(): void {
    if (!this.data.plan.id || this.isUpdatingAutoRenew()) return;

    const newValue = !this.data.plan.autoRenew;
    this.isUpdatingAutoRenew.set(true);

    this.accessPlanService.updateAutoRenew(this.data.plan.id, newValue).subscribe({
      next: (updatedPlan) => {
        this.data.plan.autoRenew = updatedPlan.autoRenew;
        const msg = newValue ? 'Renovação automática ativada!' : 'Renovação automática desativada!';
        this.snackBar.open(msg, 'Fechar', { duration: 3000 });
        this.isUpdatingAutoRenew.set(false);
      },
      error: () => {
        this.snackBar.open('Erro ao atualizar renovação automática.', 'Fechar', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.isUpdatingAutoRenew.set(false);
      }
    });
  }

  /**
   * Solicitar cobrança ao pagador externo
   */
  requestPayment(): void {
    const planId = this.data.plan.id;
    if (!planId || this.isRequestingPayment()) return;

    this.isRequestingPayment.set(true);
    this.externalPayerService.requestPayment(planId).subscribe({
      next: (updatedPlan) => {
        this.data.plan.paymentRequestedAt = updatedPlan.paymentRequestedAt;
        this.snackBar.open('Cobrança solicitada ao pagador com sucesso!', 'Fechar', {
          duration: 4000,
          panelClass: ['success-snackbar']
        });
        this.isRequestingPayment.set(false);
      },
      error: () => {
        this.snackBar.open('Erro ao solicitar cobrança. Tente novamente.', 'Fechar', { duration: 4000 });
        this.isRequestingPayment.set(false);
      }
    });
  }

  /** Plano tem pagador externo que já aceitou mas ainda não recebeu solicitação */
  canRequestPayment(): boolean {

    if(!this.hasExternalPayer()){
      return true
    }
    return !!(
      this.data.plan.payerAccepted === true &&
      !this.data.plan.paymentRequestedAt
    );
  }

  /** Cobrança já foi solicitada */
  paymentAlreadyRequested(): boolean {
    return !!this.data.plan.paymentRequestedAt;
  }

  /**
   * Get discount type label
   * Requirement: 4.5
   */
  getDiscountType(discount: AccessPlanDiscount): string {
    return discount.type || 'N/A';
  }

  /**
   * Get formatted discount value
   * Requirement: 4.5
   */
  getDiscountValue(discount: AccessPlanDiscount): string {
    return discount.description || 'N/A';
  }

  /**
   * Format currency in Brazilian format
   * Requirement: 14.1
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Calculate item total (quantity * unitPrice)
   * Requirement: 4.4
   */
  getItemTotal(quantity: number | undefined, unitPrice: number | undefined): number {
    return (quantity || 0) * (unitPrice || 0);
  }

  /**
   * Navigate to payments for this plan
   * Requirements: 4.6, 13.2
   */
  viewPayments(): void {
    this.dialogRef.close({ action: 'viewPayments', plan: this.data.plan });
  }

  /**
   * Abre modal de upgrade de plano
   */
  openUpgradeModal(): void {
    this.dialogRef.close();
    
    this.dialog.open(PlanUpgradeModalComponent, {
      width: '900px',
      maxWidth: '95vw',
      disableClose: false,
      data: { plan: this.data.plan }
    });
  }

  /**
   * Abre dialog para adicionar pagador externo
   * Requirement: 2.2 - Permitir adicionar pagador após criação do plano
   */
  openAddPayerDialog(): void {
    const dialogRef = this.dialog.open(AddPayerDialogComponent, {
      width: '480px',
      maxWidth: '95vw',
      disableClose: false
    });

    dialogRef.afterClosed().subscribe(payerCnpj => {
      if (payerCnpj) {
        this.externalPayerService.addExternalPayer(this.data.plan.id!, payerCnpj)
          .subscribe({
            next: (updatedPlan) => {
              this.data.plan = updatedPlan;
              this.snackBar.open('Pagador externo adicionado com sucesso', 'Fechar', {
                duration: 3000,
                panelClass: ['success-snackbar']
              });
            },
            error: (err) => {
              this.snackBar.open('Erro ao adicionar pagador externo', 'Fechar', {
                duration: 3000,
                panelClass: ['error-snackbar']
              });
            }
          });
      }
    });
  }

  /**
   * Verifica se o plano pode fazer upgrade
   */
  canUpgrade(): boolean {
    return !!(this.data.plan.active && this.data.plan.id);
  }

  /**
   * Verifica se existe um pagador externo:
   * payerCnpj deve estar preenchido E ser diferente do cpfcnpj do titular
   */
  hasExternalPayer(): boolean {
    const payer = this.data.plan.payerCnpj;
    return !!payer && payer.trim() !== '' && payer !== this.data.plan.cpfcnpj;
  }

  /**
   * Close modal
   * Requirement: 4.1
   */
  close(): void {
    this.dialogRef.close();
  }
}
